import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
    extractSeasonFromRow,
    getDisplayPuzzleLevel,
    isLevelLocked,
    SEASON_MAX_LEVEL,
} from "../wordWheelLevelUtils";

function describeLevelShift(fromLevel, toLevel) {
    if (!Number.isFinite(fromLevel) || !Number.isFinite(toLevel) || fromLevel === toLevel) {
        return "";
    }
    if (toLevel < fromLevel) {
        return `This puzzle becomes level ${toLevel}. Current levels ${toLevel}–${fromLevel - 1} will increase by 1 (become ${toLevel + 1}–${fromLevel}).`;
    }
    return `This puzzle becomes level ${toLevel}. Current levels ${fromLevel + 1}–${toLevel} will decrease by 1 (become ${fromLevel}–${toLevel - 1}).`;
}

export default function WordWheelSwitchLevelDialog({
    open,
    onClose,
    onConfirm,
    template,
    loading = false,
}) {
    const currentLevel = template ? getDisplayPuzzleLevel(template) : null;
    const season = template ? extractSeasonFromRow(template) : "";
    const locked = template ? isLevelLocked(template) : false;
    const [targetLevel, setTargetLevel] = useState("");

    useEffect(() => {
        if (open && currentLevel != null) {
            setTargetLevel(String(currentLevel));
        }
    }, [open, currentLevel]);

    const parsedLevel = parseInt(targetLevel, 10);
    const levelValid = Number.isFinite(parsedLevel)
        && parsedLevel >= 1
        && parsedLevel <= SEASON_MAX_LEVEL;
    const shiftPreview = levelValid ? describeLevelShift(currentLevel, parsedLevel) : "";

    const handleConfirm = () => {
        if (!levelValid) {
            return;
        }
        onConfirm(parsedLevel);
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Switch puzzle level</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        Move this puzzle to a new play order. Neighbors in the same season and language
                        shift so each level stays unique.
                    </Typography>

                    {season && (
                        <Typography variant="caption" color="text.secondary">
                            Season: {season}
                            {currentLevel != null ? ` · current level ${currentLevel}` : ""}
                        </Typography>
                    )}

                    {locked && (
                        <Alert severity="info">
                            This catalog is locked. Switching still reorders neighboring levels so the
                            season stays unique.
                        </Alert>
                    )}

                    <TextField
                        label={`New level (1–${SEASON_MAX_LEVEL})`}
                        size="small"
                        type="number"
                        fullWidth
                        value={targetLevel}
                        onChange={(e) => setTargetLevel(e.target.value)}
                        disabled={loading}
                        slotProps={{
                            htmlInput: { min: 1, max: SEASON_MAX_LEVEL, step: 1 },
                        }}
                    />

                    {shiftPreview && (
                        <Alert severity="warning">{shiftPreview}</Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={loading || !levelValid || parsedLevel === currentLevel}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Switch level
                </Button>
            </DialogActions>
        </Dialog>
    );
}
